import React from "react";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  CardActionArea, Chip, Stack, Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkIcon from "@mui/icons-material/Work";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { BRAND, SECTION_COLORS } from "../theme";

const SECTIONS = [
  {
    id: "economia",
    title: "Economía",
    icon: TrendingUpIcon,
    palette: SECTION_COLORS.economia,
    count: 8,
    desc: "PIB, IMACEC, IPC, tipo de cambio, cobre, deuda pública, FBCF y consumo de hogares.",
    fuentes: ["Banco Central", "INE"],
    to: "/economia",
  },
  {
    id: "trabajo",
    title: "Trabajo",
    icon: WorkIcon,
    palette: SECTION_COLORS.trabajo,
    count: 9,
    desc: "Desocupación, empleo, informalidad, ingresos, jornada, salario mínimo y más.",
    fuentes: ["INE – ENE", "ESI"],
    to: "/trabajo",
  },
  {
    id: "salud",
    title: "Salud",
    icon: LocalHospitalIcon,
    palette: SECTION_COLORS.salud,
    count: 5,
    desc: "Cobertura FONASA e isapres, gasto en salud, per cápita y participación en el PIB.",
    fuentes: ["FONASA", "Superdesalud", "Minsal"],
    to: "/salud",
  },
  {
    id: "migracion",
    title: "Migración",
    icon: FlightLandIcon,
    palette: SECTION_COLORS.migracion,
    count: 13,
    desc: "Residencias, expulsiones, refugio, naturalización y datos sociodemográficos de migrantes.",
    fuentes: ["SERMIG", "PDI", "INE"],
    to: "/migracion",
  },
  {
    id: "seguridad",
    title: "Seguridad",
    icon: SecurityIcon,
    palette: SECTION_COLORS.seguridad,
    count: 6,
    desc: "Homicidios, casos policiales, DMCS, causas del MP, victimización y percepción ciudadana.",
    fuentes: ["CEAD", "ENUSC", "Fiscalía"],
    to: "/seguridad",
  },
];

const HIGHLIGHTS = [
  { icon: PublicOutlinedIcon, label: "Fuentes oficiales", desc: "Banco Central, INE y organismos públicos" },
  { icon: VerifiedOutlinedIcon, label: "Series verificadas", desc: "Metodología y autoría en cada gráfico" },
  { icon: DownloadOutlinedIcon, label: "Descarga abierta", desc: "PNG y CSV listos para usar" },
];

/* Decoración del hero: red de puntos + trazo de serie de tiempo ascendente */
function HeroDecoracion() {
  return (
    <Box
      aria-hidden
      component="svg"
      viewBox="0 0 1000 560"
      preserveAspectRatio="xMaxYMid slice"
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      <defs>
        <pattern id="dotsHome" width="30" height="30" patternUnits="userSpaceOnUse">
          <circle cx="1.8" cy="1.8" r="1.8" fill="rgba(255,255,255,0.09)" />
        </pattern>
        <linearGradient id="areaHome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="fadeHome" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={BRAND.nodoBlueDk} stopOpacity="1" />
          <stop offset="35%" stopColor={BRAND.nodoBlueDk} stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect x="420" y="0" width="580" height="560" fill="url(#dotsHome)" />

      <path
        d="M430,430 C500,420 540,380 600,375 C660,370 690,320 740,300
           C790,280 820,340 870,300 C905,275 930,240 960,205 C975,188 985,175 995,160"
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="2.5"
      />
      <path
        d="M430,430 C500,420 540,380 600,375 C660,370 690,320 740,300
           C790,280 820,340 870,300 C905,275 930,240 960,205 C975,188 985,175 995,160
           L995,560 L430,560 Z"
        fill="url(#areaHome)"
      />

      {[
        [430, 430], [600, 375], [740, 300], [870, 300], [995, 160],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={i === 4 ? 5 : 3.5} fill={i === 4 ? BRAND.fesYellow : "#fff"} opacity={i === 4 ? 1 : 0.85} />
      ))}

      {/* barritas tipo gráfico de barras, esquina inferior derecha */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect
          key={i}
          x={620 + i * 26}
          y={560 - 26 - i * 16}
          width="14"
          height={26 + i * 16}
          rx="3"
          fill={i % 2 === 0 ? "rgba(255,255,255,0.16)" : BRAND.fesRed}
          opacity={i % 2 === 0 ? 1 : 0.75}
        />
      ))}

      <rect x="0" y="0" width="1000" height="560" fill="url(#fadeHome)" />
    </Box>
  );
}

export default function Home() {
  const nav = useNavigate();

  return (
    <Box>
      {/* ===== HERO ===== */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(120deg, ${BRAND.nodoBlueDk} 0%, ${BRAND.nodoBlue} 55%, #0d4fa8 100%)`,
          color: "#fff",
          py: { xs: 8, md: 12 },
        }}
      >
        <HeroDecoracion />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Chip
            label="Plataforma de datos públicos"
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.14)",
              color: "#fff",
              fontWeight: 600,
              mb: 3,
              border: "1px solid rgba(255,255,255,0.22)",
              backdropFilter: "blur(4px)",
            }}
          />

          <Typography variant="h1" component="h1" sx={{ maxWidth: 640, mb: 2.5 }}>
            Datos para<br />la Democracia
          </Typography>

          <Typography
            sx={{ maxWidth: 600, opacity: 0.92, lineHeight: 1.8, fontSize: { xs: "1rem", md: "1.1rem" } }}
          >
            Plataforma de indicadores socioeconómicos de Chile con fuentes oficiales.
            Visualizaciones interactivas descargables en PNG y CSV para investigación,
            prensa, política pública y trabajo territorial.
          </Typography>

          <Stack direction="row" spacing={1.25} mt={4.5} flexWrap="wrap" gap={1.25}>
            <Chip label="41 indicadores" sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.2)" }} />
            <Chip label="5 secciones temáticas" sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.2)" }} />
            <Chip label="Fuentes oficiales" sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.2)" }} />
            <Chip label="Descarga gratuita" sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 700, border: "1px solid rgba(255,255,255,0.2)" }} />
          </Stack>
        </Container>
      </Box>

      {/* ===== FRANJA DE CONFIANZA ===== */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BRAND.border}` }}>
        <Container maxWidth="lg">
          <Grid container>
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <Grid
                  key={h.label}
                  size={{ xs: 12, sm: 4 }}
                  sx={{
                    py: 3,
                    px: { xs: 0, sm: 3 },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    borderLeft: { sm: i > 0 ? `1px solid ${BRAND.borderSoft}` : "none" },
                  }}
                >
                  <Box
                    sx={{
                      width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
                      bgcolor: `${BRAND.nodoBlue}0F`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: BRAND.nodoBlue, fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: BRAND.text }}>
                      {h.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: BRAND.textSub }}>
                      {h.desc}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ===== TARJETAS DE SECCIÓN ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <Box sx={{ textAlign: "center", maxWidth: 620, mx: "auto", mb: 5.5 }}>
          <Typography
            sx={{
              fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em",
              textTransform: "uppercase", color: BRAND.nodoBlue, mb: 1.5,
            }}
          >
            Cinco áreas temáticas
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: "1.7rem", md: "2.1rem" }, mb: 1.5, color: BRAND.text }}>
            Explora los indicadores
          </Typography>
          <Typography sx={{ color: BRAND.textSub, lineHeight: 1.75 }}>
            Cada sección reúne indicadores con sus series de tiempo, desagregaciones y
            opciones de descarga. Fuente y autoría en cada gráfico.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const c = s.palette.main;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    transition: "all 0.25s cubic-bezier(.2,.8,.2,1)",
                    "&:hover": {
                      boxShadow: "0 20px 44px -18px rgba(9,20,50,0.28)",
                      transform: "translateY(-3px)",
                      borderColor: `${c}55`,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => nav(s.to)}
                    sx={{
                      height: "100%",
                      alignItems: "flex-start",
                      p: 0,
                      "&:hover .arrow-icon": { transform: "translateX(3px)" },
                    }}
                  >
                    <Box sx={{ height: 4, background: `linear-gradient(90deg, ${c}, ${c}66)` }} />
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: 2.5,
                          bgcolor: s.palette.soft,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          mb: 2.25,
                        }}
                      >
                        <Icon sx={{ color: c, fontSize: 26 }} />
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="h6" color={BRAND.text}>
                          {s.title}
                        </Typography>
                        <Chip
                          label={`${s.count} indicadores`}
                          size="small"
                          sx={{ bgcolor: s.palette.soft, color: c, fontWeight: 700, fontSize: "0.7rem" }}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" lineHeight={1.6} mb={2}>
                        {s.desc}
                      </Typography>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5} mb={2.25}>
                        {s.fuentes.map((f) => (
                          <Chip
                            key={f}
                            label={f}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.68rem", borderColor: BRAND.border, color: BRAND.textSub }}
                          />
                        ))}
                      </Stack>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: c }}>
                        <Typography fontWeight={700} fontSize="0.82rem">
                          Ver indicadores
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 16, transition: "transform 0.2s ease" }} className="arrow-icon" />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* ===== FOOTER INSTITUCIONAL ===== */}
      <Box sx={{ bgcolor: "#fff", borderTop: `1px solid ${BRAND.border}`, py: { xs: 5, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h6" mb={1.25} color={BRAND.nodoBlue}>
                Sobre esta plataforma
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                Esta plataforma reúne indicadores públicos de Chile en economía, trabajo, salud,
                migración y seguridad. Los datos provienen de fuentes oficiales como el Banco Central,
                el INE, FONASA, la Superintendencia de Salud, el SERMIG y el CEAD.
                Cada gráfico incluye la leyenda <em>"Elaborado por Fundación Nodo XXI"</em> y puede
                descargarse en formato PNG o CSV.
              </Typography>

              <Typography variant="h6" mt={3} mb={1.25} color={BRAND.nodoBlue}>
                Agradecimientos
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                El diseño de cada una de las secciones fue realizado con el apoyo de investigadoras
                e investigadores de Nodo XXI. Agradecemos especialmente la colaboración de Gabriel
                Carvallo, Nicolás Ratto, Matías Goyenechea, Matías Delgado y Consuelo Salas.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Divider sx={{ display: { xs: "block", md: "none" }, mb: 3 }} />
              <Stack direction="row" spacing={3} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                <Box
                  component="img"
                  src="/img/logoNodo_COLOR.png"
                  alt="Nodo XXI"
                  sx={{ height: 52, objectFit: "contain" }}
                />
                <Box
                  component="img"
                  src="/img/FES-Logo_Standard_Rot_Bildschirm.png"
                  alt="FES Chile"
                  sx={{ height: 44, objectFit: "contain" }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
